/**
 * Deferred Due Date notification logic.
 *
 * All Deferred notifications are driven by the Due Date relative to today (UTC),
 * scaled by the record's CAT:
 *  - red: on the Due Date and every day after, until the event is closed,
 *  - amber: starting a CAT-specific number of days before the Due Date,
 *  - null: otherwise (or when no Due Date / unknown CAT lead).
 *
 * The "days open" age no longer drives any flashing — only the Due Date does.
 */
import { UnscheduledEvent } from '../types';
import { EVENT_STATUS, CAT_DUE_DATE_AMBER_LEAD_DAYS } from '../constants';

/** Due Date alert severity. Red outranks amber. */
export type DeferredDueState = 'red' | 'amber' | null;

const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;

/** Today's date at UTC midnight, as milliseconds. */
function todayMidnightUtcMs(): number {
  const todayZulu = new Date().toISOString().split('T')[0];
  return new Date(`${todayZulu}T00:00:00Z`).getTime();
}

/**
 * Computes the Due Date alert state for a single Deferred event. Returns null
 * when there is no Due Date, the Due Date is unparseable, or the Due Date is
 * still further out than the CAT's amber lead window.
 */
export function getDeferredDueState(event: UnscheduledEvent): DeferredDueState {
  if (!event.dueDateZulu) return null;
  const dueDatePart = event.dueDateZulu.trim().split(/[ T]/)[0];
  if (!dueDatePart) return null;
  const dueMs = new Date(`${dueDatePart}T00:00:00Z`).getTime();
  if (Number.isNaN(dueMs)) return null;

  const daysUntilDue = Math.round((dueMs - todayMidnightUtcMs()) / MILLISECONDS_PER_DAY);
  if (daysUntilDue <= 0) return 'red';

  const amberLeadDays = CAT_DUE_DATE_AMBER_LEAD_DAYS[event.catType || ''];
  if (amberLeadDays !== undefined && daysUntilDue <= amberLeadDays) return 'amber';
  return null;
}

/**
 * Aggregates the most severe Due Date state across all active (IN_WORK/PENDING)
 * Deferred events, for the sidebar/nav highlight. Red outranks amber.
 */
export function aggregateDeferredDueState(events: UnscheduledEvent[]): DeferredDueState {
  let hasAmber = false;
  for (const event of events) {
    if (event.recordType !== 'Deferred') continue;
    if (event.status !== EVENT_STATUS.IN_WORK && event.status !== EVENT_STATUS.PENDING) continue;
    const state = getDeferredDueState(event);
    if (state === 'red') return 'red';
    if (state === 'amber') hasAmber = true;
  }
  return hasAmber ? 'amber' : null;
}
